import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Grid, TextField, Button } from '@mui/material';
import { useRefreshWeather } from 'features/weather/api/refreshWeather';
import { useState } from 'react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import useInterval from '../../hooks/useInterval';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { WeatherDetailsForm } from './types';

interface Props {
  hide?: boolean;
  onSubmit: () => void;
  setValue: UseFormSetValue<WeatherDetailsForm>;
  control: Control<WeatherDetailsForm>;
  isValid: boolean;
}

const WeatherDetailsFilters = ({
  hide,
  setValue,
  control,
  isValid,
  onSubmit,
}: Props) => {
  const [paused, setPaused] = useState(false);
  const { dateFormatWithZeros, timeFormat, ampm } = useUserDateTime();
  const { refetch: refreshWeather } = useRefreshWeather({
    config: { enabled: false },
  });
  const getWeather = async () => {
    await refreshWeather();
    setValue('toDate', new Date());
    onSubmit();
  };
  const handleClickedGetHistory = () => {
    setPaused(true);
    onSubmit();
  };
  // Refresh weather data every 15 minutes unless someone has clicked Get History
  useInterval(() => {
    if (!paused) {
      setValue('toDate', new Date());
      onSubmit();
    }
  }, 300000);

  if (hide) return <Grid item container style={{ paddingTop: '1rem' }} />;

  return (
    <Grid
      item
      container
      spacing={2}
      alignItems='center'
      style={{ paddingTop: '1rem', paddingLeft: '1rem' }}
    >
      <Grid item>
        <Controller
          name='fromDate'
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label='From'
                inputFormat={`${dateFormatWithZeros} ${timeFormat}`}
                value={value}
                onChange={onChange}
                renderInput={(props) => <TextField {...props} />}
                ampm={ampm}
              />
            </LocalizationProvider>
          )}
        />
      </Grid>
      <Grid item>
        <Controller
          name='toDate'
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label='To'
                inputFormat={`${dateFormatWithZeros} ${timeFormat}`}
                value={value}
                onChange={onChange}
                renderInput={(props) => <TextField {...props} />}
                ampm={ampm}
              />
            </LocalizationProvider>
          )}
        />
      </Grid>
      <Grid item style={{ minWidth: '10rem' }}>
        <Button
          onClick={handleClickedGetHistory}
          fullWidth
          variant='contained'
          disabled={!isValid}
        >
          Get History
        </Button>
      </Grid>
      {process.env.REACT_APP_MODE === 'LPT' && (
        <Grid item style={{ minWidth: '10rem' }}>
          <Button onClick={getWeather} fullWidth variant='contained'>
            Refresh Weather
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default WeatherDetailsFilters;
